import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import Loader from '../../Components/Loader/Loader';
import { serviceConsumer } from '../../network/ServiceConsumer';
import { toDateInput } from './dateInput';

// Oireachtas Event Details tab — functional match to the create-oireachtas
// design (About event + Event dates sections; region-derived currency; the
// three deadlines). Purpose-built (not the feis/grade dual-mode form). The
// organizer is chosen pre-wizard and read from localStorage.organizerId.
const REGIONS = [
  'Eastern Canada', 'Western Canada', 'New England', 'Mid-Atlantic',
  'Southern', 'Mid-America', 'Western US',
];
const CANADIAN = new Set(['eastern canada', 'western canada']);
const currencyForRegion = (r) => (!r ? '' : CANADIAN.has(r.trim().toLowerCase()) ? 'CAD' : 'USD');

const EventDetailsFies = (props) => {
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const EventID = localStorage.getItem('ID');
  const organizerId = localStorage.getItem('organizerId') || '';

  const [form, setForm] = useState({
    name: '', contact_email: '', region: '',
    opening_date: '', entriesCloseDate: '', lateEntryCloseDate: '', changeFreezeDate: '',
    eventStartDate: '', eventEndDate: '',
  });
  // Read-only: the API derives it from the venue (never sent back from here).
  const [timeZone, setTimeZone] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    (async () => {
      if (!EventID) return;
      try {
        setLoading(true);
        const res = await serviceConsumer('GET', `${process.env.REACT_APP_BASE_URL}/oireachtas/${EventID}`);
        const ev = res.event;
        if (ev) {
          setIsEdit(true);
          setTimeZone(ev.timeZone || '');
          setForm({
            name: ev.name || '', contact_email: ev.contact_email || '', region: ev.region || '',
            opening_date: toDateInput(ev.opening_date), entriesCloseDate: toDateInput(ev.entriesCloseDate),
            lateEntryCloseDate: toDateInput(ev.lateEntryCloseDate), changeFreezeDate: toDateInput(ev.changeFreezeDate),
            eventStartDate: toDateInput(ev.eventDays?.[0]), eventEndDate: toDateInput(ev.eventDays?.[1]),
          });
        }
      } catch (e) { /* new */ } finally { setLoading(false); }
    })();
  }, [EventID]);

  const isValid = form.name && form.region && form.opening_date && form.entriesCloseDate;
  const currency = currencyForRegion(form.region);

  const advance = () => {
    localStorage.setItem('TAB', 2);
    if (Number(localStorage.getItem('page')) < 1) localStorage.setItem('page', 1);
    props.setEventName(form.name);
    setTimeout(() => props.setValue('2'), 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    const eventDays = [form.eventStartDate, form.eventEndDate].filter(Boolean);
    const payload = {
      name: form.name, contact_email: form.contact_email, region: form.region,
      opening_date: form.opening_date || null,
      entriesCloseDate: form.entriesCloseDate || null,
      lateEntryCloseDate: form.lateEntryCloseDate || null,
      changeFreezeDate: form.changeFreezeDate || null,
      eventDays,
    };
    try {
      setLoading(true);
      if (isEdit && EventID) {
        const res = await serviceConsumer('PUT', `${process.env.REACT_APP_BASE_URL}/oireachtas/${EventID}`, payload);
        if (res.success) advance();
      } else {
        if (!organizerId) throw new Error('No organizer selected. Start from "Create new Oireachtas".');
        const res = await serviceConsumer('POST', `${process.env.REACT_APP_BASE_URL}/oireachtas`, { ...payload, organizer_id: organizerId, page: 1, live: false });
        if (res.success) { localStorage.setItem('ID', res.event._id); advance(); }
      }
    } catch (err) {
      toast.error(err?.messages || err?.message || 'Failed to save event. Please try again.');
    } finally { setLoading(false); }
  };

  const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', maxWidth: 900 };
  const lbl = { fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 6 };
  const inp = { width: '100%', padding: '9px 12px' };
  const hint = { fontSize: 12, color: '#797979', marginTop: 4 };

  // NOTE: a plain function returning JSX (called inline), NOT a nested
  // component. Defining a component inside the render remounts the input on
  // every keystroke and drops focus.
  const field = (label, k, type = 'text', help) => (
    <div className="mb-3" key={k}>
      <label style={lbl}>{label}</label>
      <input className="dashboard-input" type={type} value={form[k]} onChange={set(k)} style={inp} />
      {help && <div style={hint}>{help}</div>}
    </div>
  );

  return (
    <div>
      {loading && <Loader />}
      {!loading && (
        <form className="form form-label-right" autoComplete="off" onSubmit={submit}>
          <div className="detailsHeaders">
            <h4 className="eventPage">Event Details</h4>
            <p className="eventDescription">This name becomes the event title once saved.</p>
          </div>

          <h5 style={{ fontWeight: 700, marginTop: 8, marginBottom: 16 }}>About event</h5>
          <div style={grid}>
            {field('Event Name', 'name')}
            {field('Contact Email', 'contact_email', 'email')}
            <div className="mb-3">
              <label style={lbl}>Region</label>
              <select className="dashboard-input" value={form.region} onChange={set('region')} style={inp}>
                <option value="">Select region</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="mb-3">
              <label style={lbl}>Currency</label>
              <input className="dashboard-input" value={currency} readOnly disabled style={{ ...inp, background: '#f8f9fa' }} />
              <div style={hint}>Set automatically by the selected region.</div>
            </div>
          </div>

          <h5 style={{ fontWeight: 700, marginTop: 24, marginBottom: 16 }}>Event dates</h5>
          <div className="mb-3" style={hint}>
            {timeZone
              ? `Close dates end at 11:59 pm ${timeZone} time, set from the venue.`
              : "Close dates end at 11:59 pm in the venue's time zone (set on the Venue tab)."}
          </div>
          <div style={grid}>
            {field('Registration Open Date', 'opening_date', 'date')}
            {field('Entries Close Date', 'entriesCloseDate', 'date')}
            {field('Late Entry Close Date', 'lateEntryCloseDate', 'date', 'Late entries incur a surcharge.')}
            {field('Changes Freeze Date', 'changeFreezeDate', 'date', 'No edits after this date.')}
            {field('Event Start Date', 'eventStartDate', 'date')}
            {field('Event End Date', 'eventEndDate', 'date')}
          </div>

          <div className="mt-4 mb-5" style={{ maxWidth: 900, textAlign: 'right' }}>
            <button className={isValid ? 'filled' : 'disabled'} type="submit" disabled={!isValid}>Next</button>
          </div>
        </form>
      )}
      <ToastContainer />
    </div>
  );
};

export default EventDetailsFies;
